import { VERIFICATION_TYPES } from 'helper/constant';

import {
    NODE_SIZE,
    GAP,
    ADDITIONAL_CHECKS,
    COLORS,
    IDENTITY_METHOD_ORDER,
    LABELS,
    MARKER_END,
    NODE_IDS,
    CHECK_TYPES,
} from './constants';

// Creates a ReactFlow-compatible node object.
// Nodes inside the identity group must supply parentId so ReactFlow constrains
// them within the group boundary (extent: "parent").
function makeNode({ id, type, position, data, style = {}, parentId }) {
    return {
        id,
        type,
        position,
        data,
        style,
        ...(parentId ? { parentId, extent: 'parent' } : {}),
        draggable: false,
        selectable: false,
    };
}

// Creates a base edge with shared defaults (gray stroke, arrow marker, zIndex).
// style / data overrides are merged in by callers.
function makeEdge({
    id,
    source,
    target,
    sourceHandle = 'source-bottom',
    targetHandle = 'target-top',
    type = 'yesno',
    label = '',
    style = {},
    data = {},
}) {
    return {
        id,
        source,
        target,
        sourceHandle,
        targetHandle,
        type,
        label,
        style: { stroke: COLORS.EDGE_GRAY, strokeWidth: 2, ...style },
        markerEnd: MARKER_END,
        zIndex: 5,
        data,
    };
}

// Builds an AND/OR connector edge between two adjacent identity method nodes.
// Uses the "logical" edge type (horizontal, no arrowhead).
function makeLogicalEdge({ source, target, label }) {
    const edge = makeEdge({
        id: `${source}-to-${target}`,
        source,
        target,
        sourceHandle: 'source-right',
        targetHandle: 'target-left',
        type: 'logical',
        label,
    });
    delete edge.markerEnd; // AND/OR connectors have no arrowhead
    return edge;
}

// Builds a Yes/No decision branch edge.
// fieldType identifies the decision node and drives color-inversion logic in YesNoEdge
// (e.g. watchlist decisions where "Yes" = bad outcome).
function makeYesNoEdge({
    id,
    source,
    target,
    label = '',
    sourceHandle = 'source-bottom',
    targetHandle = 'target-top',
    fieldType = '',
}) {
    return makeEdge({
        id: id || `${source}-to-${target}`,
        source,
        target,
        sourceHandle,
        targetHandle,
        type: 'yesno',
        label,
        data: fieldType ? { fieldType } : {},
    });
}

// ─── Identity method builders ─────────────────────────────────────────────────

// Builds a single identity method node (govt_id, kba, bank, or selfie).
// The position is computed by the caller; parentId places the node inside the group.
function buildIdentityMethodNode(key, position, parentId) {
    return makeNode({
        id: `node_${key}`,
        type: 'identityMethod',
        position,
        data: { type: key, label: LABELS[key] },
        parentId,
    });
}

// Builds the horizontal AND/OR connector edge between two adjacent method nodes.
function buildMethodConnectorEdge(sourceId, targetId, label) {
    const edge = makeLogicalEdge({ source: sourceId, target: targetId, label });
    return edge;
}

// ─── Liveness layout computer ─────────────────────────────────────────────────
//
// Pre-computes all Y/X positions for the selfie + liveness sub-section inside
// the identity group. Keeping the math here (separate from node construction)
// makes it easier to adjust spacing without touching builder logic.
//
// T-fork strategy for Yes/No terminals:
//   "Clear" is placed ABOVE the decision center  → Yes branch curves up
//   "Needs Review" is placed BELOW the decision center → No branch curves down
//   decision center = midpoint(clear center, review center)
//   This causes getSmoothStepPath to produce a symmetric T-fork for both branches.
//
// T-fork strategy for selfie → decisions:
//   selfieY is derived so selfie center = midpoint(faceMatch center, liveness center)
//   This produces a balanced T-fork from selfie to both decision nodes.

function computeLivenessLayout({ P, MW, MH, DW, DH, TW, TH }) {
    const TGAP = GAP.TERM_V_OFFSET;

    // Decisions sit to the right of the selfie node
    const decisionX = P + MW + GAP.LIVENESS_DEC_OFFSET;
    // Terminals sit to the right of the decision nodes
    const terminalX = decisionX + DW + GAP.TERM_H_OFFSET;

    // Face match decision aligns with the top of the selfie area
    const faceY = P + MH + GAP.SELFIE_V;

    // Clear terminal: above face match decision center
    const clear1Y = faceY - TGAP - TH;
    // Needs Review terminal: below face match decision bottom
    const review1Y = faceY + DH + TGAP;

    // Liveness decision sits below the face match Needs Review terminal,
    // leaving enough room so clear_2 won't overlap review_1.
    const livenessY = review1Y + TH + TGAP + TH + GAP.LIVENESS_V;

    const clear2Y = livenessY - TGAP - TH;
    const review2Y = livenessY + DH + TGAP;

    // Center selfie between face match and liveness decisions for a balanced T-fork
    const selfieY = Math.round((faceY + DH / 2 + livenessY + DH / 2) / 2 - MH / 2);

    return {
        decisionX,
        terminalX,
        faceY,
        clear1Y,
        review1Y,
        livenessY,
        clear2Y,
        review2Y,
        selfieY,
    };
}

// ─── Liveness sub-section builders ───────────────────────────────────────────
// Each returns { nodes, edges } and does not mutate any shared state.
// Positions are provided by computeLivenessLayout; parentId places nodes in the group.

function buildFaceMatchSection({ decisionX, faceY, terminalX, clear1Y, review1Y }, parentId) {
    return {
        nodes: [
            makeNode({
                id: NODE_IDS.DECISION_FACE_MATCH,
                type: 'decision',
                position: { x: decisionX, y: faceY },
                data: { label: LABELS.FACE_MATCH_PASSED },
                parentId,
            }),
            makeNode({
                id: NODE_IDS.TERMINAL_FM_CLEAR,
                type: 'terminal',
                position: {
                    x: terminalX + GAP.FACE_MATCH_TERM_X_ADJUST,
                    y: clear1Y + GAP.LIVENESS_TERM_Y_INSET,
                },
                data: { label: LABELS.CLEAR, variant: 'clear' },
                parentId,
            }),
            makeNode({
                id: NODE_IDS.TERMINAL_FM_REVIEW,
                type: 'terminal',
                position: {
                    x: terminalX + GAP.FACE_MATCH_TERM_X_ADJUST,
                    y: review1Y - GAP.LIVENESS_TERM_Y_INSET,
                },
                data: { label: LABELS.NEEDS_REVIEW, variant: 'needsReview' },
                parentId,
            }),
        ],
        edges: [
            // Selfie → Face Match decision (horizontal connection)
            makeYesNoEdge({
                id: 'edge_selfie_facematch',
                source: NODE_IDS.SELFIE,
                target: NODE_IDS.DECISION_FACE_MATCH,
                sourceHandle: 'source-right',
                targetHandle: 'target-left',
            }),
            // Face Match Yes → Clear
            makeYesNoEdge({
                id: 'edge_facematch_clear',
                source: NODE_IDS.DECISION_FACE_MATCH,
                target: NODE_IDS.TERMINAL_FM_CLEAR,
                label: 'Yes',
                sourceHandle: 'source-right',
                targetHandle: 'target-left',
            }),
            // Face Match No → Needs Review
            makeYesNoEdge({
                id: 'edge_facematch_review',
                source: NODE_IDS.DECISION_FACE_MATCH,
                target: NODE_IDS.TERMINAL_FM_REVIEW,
                label: 'No',
                sourceHandle: 'source-right',
                targetHandle: 'target-left',
            }),
        ],
    };
}

function buildLivenessSection({ decisionX, livenessY, terminalX, clear2Y, review2Y }, parentId) {
    return {
        nodes: [
            makeNode({
                id: NODE_IDS.DECISION_LIVENESS,
                type: 'decision',
                position: { x: decisionX, y: livenessY },
                data: { label: LABELS.LIVENESS_CONFIDENCE_SCORE_HIGH },
                parentId,
            }),
            makeNode({
                id: NODE_IDS.TERMINAL_LIV_CLEAR,
                type: 'terminal',
                position: {
                    x: terminalX + GAP.LIVENESS_TERM_X_ADJUST,
                    y: clear2Y + GAP.LIVENESS_TERM_Y_INSET,
                },
                data: { label: LABELS.CLEAR, variant: 'clear' },
                parentId,
            }),
            makeNode({
                id: NODE_IDS.TERMINAL_LIV_REVIEW,
                type: 'terminal',
                position: {
                    x: terminalX + GAP.LIVENESS_TERM_X_ADJUST,
                    y: review2Y - GAP.LIVENESS_TERM_Y_INSET,
                },
                data: { label: LABELS.NEEDS_REVIEW, variant: 'needsReview' },
                parentId,
            }),
        ],
        edges: [
            // Selfie → Liveness decision (horizontal, T-fork from same selfie source)
            makeYesNoEdge({
                id: 'edge_selfie_liveness',
                source: NODE_IDS.SELFIE,
                target: NODE_IDS.DECISION_LIVENESS,
                sourceHandle: 'source-right',
                targetHandle: 'target-left',
            }),
            // Liveness Yes → Clear
            makeYesNoEdge({
                id: 'edge_liveness_clear',
                source: NODE_IDS.DECISION_LIVENESS,
                target: NODE_IDS.TERMINAL_LIV_CLEAR,
                label: 'Yes',
                sourceHandle: 'source-right',
                targetHandle: 'target-left',
            }),
            // Liveness No → Needs Review
            makeYesNoEdge({
                id: 'edge_liveness_review',
                source: NODE_IDS.DECISION_LIVENESS,
                target: NODE_IDS.TERMINAL_LIV_REVIEW,
                label: 'No',
                sourceHandle: 'source-right',
                targetHandle: 'target-left',
            }),
        ],
    };
}

// ─── Identity section assembler ───────────────────────────────────────────────
//
// Builds the entire Identity Proof group and everything inside it.
// Layout with liveness enabled:
//
//   ┌────────────────────────────────────────────────────────────────────────┐
//   │  IDENTITY PROOF                                                        │
//   │  [Govt ID] ─OR/AND─ [KBA] ─OR/AND─ [Bank]                            │
//   │      ↓ AND                                                             │
//   │  [Selfie] ───────────→ [FACE MATCH PASSED?]  → Yes → [Clear]         │
//   │                                               → No  → [Needs Review]  │
//   │                         [LIVENESS SCORE HIGH?] → Yes → [Clear]        │
//   │                                                → No  → [Needs Review] │
//   └────────────────────────────────────────────────────────────────────────┘
//
// Group dimensions are computed dynamically based on:
//   - How many identity methods are enabled (determines group width)
//   - Whether liveness is enabled (determines group height)
// The group's final canvas position is set by prepareChartData after build.

function buildIdentitySection(idVerificationsObj) {
    const { verification_option, ...methods } = idVerificationsObj;

    // AND/OR label depends on whether the workflow requires ALL methods or just ONE
    const connectorLabel = verification_option;

    // Liveness is only available when govt_id is enabled and its liveness sub-option is on
    const livenessEnabled =
        methods.govt_id?.enabled && methods.govt_id?.metadata?.livenessCheck?.enabled;

    // Filter to only the enabled methods, preserving left-to-right order
    const enabledMethods = IDENTITY_METHOD_ORDER.filter((key) => methods[key]?.enabled);

    // Shorthand size variables used throughout layout math
    const P = GAP.GROUP_PADDING;
    const MW = NODE_SIZE.IDENTITY_METHOD.width;
    const MH = NODE_SIZE.IDENTITY_METHOD.height;
    const MG = GAP.IDENTITY_H;
    const DW = NODE_SIZE.DECISION.width;
    const DH = NODE_SIZE.DECISION.height;
    const TW = NODE_SIZE.TERMINAL.width;
    const TH = NODE_SIZE.TERMINAL.height;

    const methodsRowWidth = enabledMethods.length * MW + (enabledMethods.length - 1) * MG;

    // Liveness layout is always computed because its terminal positions
    // determine the minimum group height/width even when liveness is disabled.
    const livenessLayout = computeLivenessLayout({ P, MW, MH, DW, DH, TW, TH });
    const {
        decisionX,
        terminalX,
        faceY,
        clear1Y,
        review1Y,
        livenessY,
        clear2Y,
        review2Y,
        selfieY,
    } = livenessLayout;

    // Group dimensions expand to fit liveness terminals when liveness is active
    const groupWidth = livenessEnabled
        ? Math.max(methodsRowWidth + P * 2, terminalX + TW + P)
        : methodsRowWidth + P * 2;

    const groupHeight = livenessEnabled ? review2Y + TH + P : MH + P * 2;

    // ── Group container ──────────────────────────────────────────────────────
    // Position is placeholder (x:0, y:0) — prepareChartData centers it on canvas.
    const groupNode = makeNode({
        id: NODE_IDS.GROUP_IDENTITY,
        type: 'group',
        position: { x: 0, y: 0 },
        data: { label: 'Identity Proof' },
        style: {
            width: groupWidth,
            height: groupHeight,
            backgroundColor: '#fff',
            borderRadius: 12,
            border: '1.5px solid #E5E7EB',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            zIndex: 1,
        },
    });

    // ── Identity method nodes ────────────────────────────────────────────────
    // Each method is built individually (not in a loop) so that each is explicit
    // and easy to trace when reading or debugging the output node list.
    const methodPositions = Object.fromEntries(
        enabledMethods.map((key, idx) => [key, { x: P + idx * (MW + MG), y: P + 14 }])
    );

    const govtIdNode = methodPositions.govt_id
        ? buildIdentityMethodNode('govt_id', methodPositions.govt_id, NODE_IDS.GROUP_IDENTITY)
        : null;
    const kbaNode = methodPositions.kba
        ? buildIdentityMethodNode('kba', methodPositions.kba, NODE_IDS.GROUP_IDENTITY)
        : null;
    const bankNode = methodPositions.bank
        ? buildIdentityMethodNode('bank', methodPositions.bank, NODE_IDS.GROUP_IDENTITY)
        : null;

    // ── AND/OR connector edges between adjacent enabled methods ──────────────
    const connector01 =
        enabledMethods.length >= 2
            ? buildMethodConnectorEdge(
                  `node_${enabledMethods[0]}`,
                  `node_${enabledMethods[1]}`,
                  connectorLabel
              )
            : null;

    const connector12 =
        enabledMethods.length >= 3
            ? buildMethodConnectorEdge(
                  `node_${enabledMethods[1]}`,
                  `node_${enabledMethods[2]}`,
                  connectorLabel
              )
            : null;

    // ── Selfie node + AND edge from govt_id ─────────────────────────────────
    // Selfie is always connected from govt_id with an AND edge because
    // selfie is a mandatory follow-up to government ID verification.
    const selfieNode = livenessEnabled
        ? makeNode({
              id: NODE_IDS.SELFIE,
              type: 'identityMethod',
              position: { x: P, y: selfieY },
              data: { type: 'selfie', label: LABELS.selfie },
              parentId: NODE_IDS.GROUP_IDENTITY,
          })
        : null;

    const govtIdToSelfieEdge = livenessEnabled
        ? makeEdge({
              id: 'edge_govtid_selfie',
              source: NODE_IDS.GOVT_ID,
              target: NODE_IDS.SELFIE,
              sourceHandle: 'source-bottom',
              targetHandle: 'target-top',
              type: 'logical',
              label: 'AND',
          })
        : null;

    // ── Face match + liveness decision sub-sections ──────────────────────────
    const faceMatchSection = livenessEnabled
        ? buildFaceMatchSection(
              { decisionX, faceY, terminalX, clear1Y, review1Y },
              NODE_IDS.GROUP_IDENTITY
          )
        : null;

    const livenessSection = livenessEnabled
        ? buildLivenessSection(
              { decisionX, livenessY, terminalX, clear2Y, review2Y },
              NODE_IDS.GROUP_IDENTITY
          )
        : null;

    return {
        nodes: [
            groupNode,
            govtIdNode,
            kbaNode,
            bankNode,
            selfieNode,
            ...(faceMatchSection?.nodes ?? []),
            ...(livenessSection?.nodes ?? []),
        ].filter(Boolean),
        edges: [
            connector01,
            connector12,
            govtIdToSelfieEdge,
            ...(faceMatchSection?.edges ?? []),
            ...(livenessSection?.edges ?? []),
        ].filter(Boolean),
        groupWidth,
        groupHeight,
    };
}

// ─── Additional checks layout computer ────────────────────────────────────────
//
// Pre-computes all node positions for every additional check branch.
// Separating layout math from node construction makes spacing adjustments easy
// without touching the builder logic — just change values in ADDITIONAL_CHECKS.
//
// Supports two layout modes:
//   T-fork mode  — both Global Watchlist AND Auth DB are enabled:
//                  checks spread left (GW) and right (AuthDB) of center
//   Single mode  — only one of GW or AuthDB is enabled:
//                  that check is centered directly below the Identity Proof decision
//
// 7-Year Criminal always appears sequentially below Auth DB (never standalone).
//
// Returns a nested position map consumed by each check's builder function.

function computeAdditionalChecksLayout({
    identityProofX,
    identityProofY,
    hasGlobalWatchlist,
    hasAuthDb,
}) {
    const DW = NODE_SIZE.DECISION.width;
    const DH = NODE_SIZE.DECISION.height;
    const CW = NODE_SIZE.CHECK.width;
    const CH = NODE_SIZE.CHECK.height;
    const TW = NODE_SIZE.TERMINAL.width;
    const TH = NODE_SIZE.TERMINAL.height;
    const CV = GAP.CHECK_V;
    const SV = GAP.SECTION_V;
    const THO = GAP.TERM_H_OFFSET;
    const BH = GAP.BRANCH_H;
    const AC = ADDITIONAL_CHECKS;

    const identityProofCenterX = identityProofX + DW / 2;
    const checksY = identityProofY + DH + SV;

    // Single-check mode: center the lone check directly below identity proof
    const isSingleCheck = !(hasGlobalWatchlist && hasAuthDb);
    const singleCheckX = identityProofCenterX - CW / 2 + AC.SINGLE_CHECK_X_ADJ;

    // ── Global Watchlist (left branch) ──────────────────────────────────────
    const gwCheckX = isSingleCheck
        ? singleCheckX
        : identityProofCenterX - BH - CW - AC.BRANCH_X_SPREAD;
    const gwDecisionX = gwCheckX + (CW - DW) / 2 + AC.GW_DECISION_X_ADJ;
    const gwDecisionY = checksY + CH + CV + AC.CHECK_DECISION_V_EXTRA;

    // ── Authoritative Database (right branch) ────────────────────────────────
    const authDbCheckX = isSingleCheck
        ? singleCheckX
        : identityProofCenterX + BH + AC.BRANCH_X_SPREAD;
    const authDbDecisionX = authDbCheckX + (CW - DW) / 2 + AC.AUTH_DECISION_X_ADJ;
    const authDbDecisionY = checksY + CH + CV + AC.CHECK_DECISION_V_EXTRA;

    // ── 7 Year Criminal (sequential below auth_db) ──────────────────────────
    const sevenYearCheckY = authDbDecisionY + DH + SV + TH - AC.SYR_CHECK_V_REDUCE;
    const sevenYearDecisionX = authDbDecisionX + AC.SYR_DECISION_X_ADJ;
    const sevenYearDecisionY = sevenYearCheckY + CH + CV + AC.SYR_DECISION_V_EXTRA;

    return {
        globalWatchlist: {
            checkX: gwCheckX,
            checksY,
            decisionX: gwDecisionX,
            decisionY: gwDecisionY,
            reviewX: gwDecisionX - TW - THO - AC.GW_REVIEW_X_EXTRA,
            reviewY: gwDecisionY + (DH - TH) / 2,
            clearX: gwDecisionX + (DW - TW) / 2 + AC.GW_CLEAR_X_ADJ,
            clearY: gwDecisionY + DH + CV + AC.CLEAR_TERM_V_EXTRA,
        },
        authDb: {
            checkX: authDbCheckX,
            checksY,
            decisionX: authDbDecisionX,
            decisionY: authDbDecisionY,
            errorX: authDbDecisionX + DW + THO + AC.AUTH_ERROR_X_EXTRA,
            errorY: authDbDecisionY + (DH - TH) / 2,
            clearX: authDbDecisionX + (DW - TW) / 2 + AC.AUTH_CLEAR_X_ADJ,
            clearY: authDbDecisionY + DH + CV + AC.CLEAR_TERM_V_EXTRA,
        },
        sevenYear: {
            checkX: authDbCheckX,
            checkY: sevenYearCheckY,
            decisionX: sevenYearDecisionX,
            decisionY: sevenYearDecisionY,
            reviewX: authDbDecisionX + DW + THO + AC.SYR_REVIEW_X_EXTRA,
            reviewY: sevenYearDecisionY + (DH - TH) / 2,
            clearX: authDbDecisionX + (DW - TW) / 2 + AC.SYR_CLEAR_X_ADJ,
            clearY: sevenYearDecisionY + DH + CV + AC.CLEAR_TERM_V_EXTRA,
        },
    };
}

// ─── Additional checks sub-section builders ───────────────────────────────────
// Each accepts pre-computed positions from computeAdditionalChecksLayout.
// Each returns { nodes, edges } — no mutation of shared state.

// Global Watchlist check:
//   Identity Proof Yes → [GW Check] → [GW Decision]
//   GW Decision Yes (record found = bad) → [Needs Review]  (inverted colors via fieldType)
//   GW Decision No  (no record = good)   → [Clear]
function buildGlobalWatchlistSection({
    checkX,
    checksY,
    decisionX,
    decisionY,
    reviewX,
    reviewY,
    clearX,
    clearY,
}) {
    return {
        nodes: [
            makeNode({
                id: NODE_IDS.CHECK_GLOBAL_WATCHLIST,
                type: 'check',
                position: { x: checkX, y: checksY },
                data: { type: CHECK_TYPES.GLOBAL_WATCHLIST, label: LABELS.global_watchlist },
            }),
            makeNode({
                id: NODE_IDS.DECISION_GLOBAL_WATCHLIST,
                type: 'decision',
                position: { x: decisionX, y: decisionY },
                data: { label: LABELS.GLOBAL_WATCHLIST_RECORD_FOUND },
            }),
            makeNode({
                id: NODE_IDS.TERMINAL_GW_REVIEW,
                type: 'terminal',
                position: { x: reviewX, y: reviewY },
                data: { label: LABELS.NEEDS_REVIEW, variant: 'needsReview' },
            }),
            makeNode({
                id: NODE_IDS.TERMINAL_GW_CLEAR,
                type: 'terminal',
                position: { x: clearX, y: clearY },
                data: { label: LABELS.CLEAR, variant: 'clear' },
            }),
        ],
        edges: [
            // Identity Proof Yes → GW Check
            makeYesNoEdge({
                id: 'edge_identityproof_global_watchlist',
                source: NODE_IDS.DECISION_IDENTITY_PROOF,
                target: NODE_IDS.CHECK_GLOBAL_WATCHLIST,
                label: 'Yes',
                sourceHandle: 'source-bottom',
                targetHandle: 'target-top',
            }),
            // GW Check → GW Decision
            makeYesNoEdge({
                id: 'edge_global_watchlist_decision',
                source: NODE_IDS.CHECK_GLOBAL_WATCHLIST,
                target: NODE_IDS.DECISION_GLOBAL_WATCHLIST,
                sourceHandle: 'source-bottom',
                targetHandle: 'target-top',
            }),
            // GW Decision Yes (record found) → Needs Review  [colors inverted via fieldType]
            makeYesNoEdge({
                id: 'edge_global_watchlist_review',
                source: NODE_IDS.DECISION_GLOBAL_WATCHLIST,
                target: NODE_IDS.TERMINAL_GW_REVIEW,
                label: 'Yes',
                sourceHandle: 'source-left',
                targetHandle: 'target-right',
                fieldType: LABELS.GLOBAL_WATCHLIST_RECORD_FOUND,
            }),
            // GW Decision No (no record) → Clear  [colors inverted via fieldType]
            makeYesNoEdge({
                id: 'edge_global_watchlist_clear',
                source: NODE_IDS.DECISION_GLOBAL_WATCHLIST,
                target: NODE_IDS.TERMINAL_GW_CLEAR,
                label: 'No',
                sourceHandle: 'source-bottom',
                targetHandle: 'target-top',
                fieldType: LABELS.GLOBAL_WATCHLIST_RECORD_FOUND,
            }),
        ],
    };
}

// Authoritative Database check:
//   Identity Proof Yes → [AuthDB Check] → [AuthDB Decision]
//   AuthDB Decision Yes (check passed) → [Seven Year check] (if enabled) or [Clear]
//   AuthDB Decision No  (check failed) → [End - Check Error]
//
// The Clear terminal is only created when Seven Year is NOT present,
// because when it IS present, the Yes branch flows into the Seven Year check instead.
function buildAuthDbSection(
    { checkX, checksY, decisionX, decisionY, errorX, errorY, clearX, clearY },
    hasSevenYear
) {
    const clearTerminalNode = !hasSevenYear
        ? makeNode({
              id: NODE_IDS.TERMINAL_AUTH_DB_CLEAR,
              type: 'terminal',
              position: { x: clearX, y: clearY },
              data: { label: LABELS.CLEAR, variant: 'clear' },
          })
        : null;

    const clearEdge = !hasSevenYear
        ? makeYesNoEdge({
              id: 'edge_authoritative_db_clear',
              source: NODE_IDS.DECISION_AUTH_DB,
              target: NODE_IDS.TERMINAL_AUTH_DB_CLEAR,
              label: 'Yes',
              sourceHandle: 'source-bottom',
              targetHandle: 'target-top',
          })
        : null;

    return {
        nodes: [
            makeNode({
                id: NODE_IDS.CHECK_AUTH_DB,
                type: 'check',
                position: { x: checkX, y: checksY },
                data: {
                    type: CHECK_TYPES.AUTHORITATIVE_DB,
                    label: LABELS.authoritative_db,
                },
            }),
            makeNode({
                id: NODE_IDS.DECISION_AUTH_DB,
                type: 'decision',
                position: { x: decisionX, y: decisionY },
                data: { label: LABELS.AUTHORITATIVE_DB_CHECK_PASSED },
            }),
            makeNode({
                id: NODE_IDS.TERMINAL_AUTH_DB_ERROR,
                type: 'terminal',
                position: { x: errorX, y: errorY },
                data: { label: LABELS.END_CHECK_ERROR, variant: 'checkError' },
            }),
            clearTerminalNode,
        ].filter(Boolean),
        edges: [
            // Identity Proof Yes → AuthDB Check
            makeYesNoEdge({
                id: 'edge_identityproof_authoritative_db',
                source: NODE_IDS.DECISION_IDENTITY_PROOF,
                target: NODE_IDS.CHECK_AUTH_DB,
                label: 'Yes',
                sourceHandle: 'source-bottom',
                targetHandle: 'target-top',
            }),
            // AuthDB Check → AuthDB Decision
            makeYesNoEdge({
                id: 'edge_authoritative_db_decision',
                source: NODE_IDS.CHECK_AUTH_DB,
                target: NODE_IDS.DECISION_AUTH_DB,
                sourceHandle: 'source-bottom',
                targetHandle: 'target-top',
            }),
            // AuthDB Decision No → End Check Error (exits right)
            makeYesNoEdge({
                id: 'edge_authoritative_db_error',
                source: NODE_IDS.DECISION_AUTH_DB,
                target: NODE_IDS.TERMINAL_AUTH_DB_ERROR,
                label: 'No',
                sourceHandle: 'source-right',
                targetHandle: 'target-left',
            }),
            clearEdge,
        ].filter(Boolean),
    };
}

// Seven Year Criminal check (always sequential below Auth DB):
//   AuthDB Decision Yes → [Seven Year Check] → [Seven Year Decision]
//   SYR Decision Yes (record found = bad) → [Needs Review]  (inverted colors via fieldType)
//   SYR Decision No  (no record = good)   → [Clear]
function buildSevenYearSection({
    checkX,
    checkY,
    decisionX,
    decisionY,
    reviewX,
    reviewY,
    clearX,
    clearY,
}) {
    return {
        nodes: [
            makeNode({
                id: NODE_IDS.CHECK_SEVEN_YEAR,
                type: 'check',
                position: { x: checkX, y: checkY },
                data: { type: CHECK_TYPES.SEVEN_YEAR, label: LABELS.seven_year_criminal_report },
            }),
            makeNode({
                id: NODE_IDS.DECISION_SEVEN_YEAR,
                type: 'decision',
                position: { x: decisionX, y: decisionY },
                data: { label: LABELS.SEVEN_YEAR_CRIMINAL_RECORD_FOUND },
            }),
            makeNode({
                id: NODE_IDS.TERMINAL_SYR_REVIEW,
                type: 'terminal',
                position: { x: reviewX, y: reviewY },
                data: { label: LABELS.NEEDS_REVIEW, variant: 'needsReview' },
            }),
            makeNode({
                id: NODE_IDS.TERMINAL_SYR_CLEAR,
                type: 'terminal',
                position: { x: clearX, y: clearY },
                data: { label: LABELS.CLEAR, variant: 'clear' },
            }),
        ],
        edges: [
            // AuthDB Decision Yes → Seven Year Check
            makeYesNoEdge({
                id: 'edge_authoritative_db_to_seven_year',
                source: NODE_IDS.DECISION_AUTH_DB,
                target: NODE_IDS.CHECK_SEVEN_YEAR,
                label: 'Yes',
                sourceHandle: 'source-bottom',
                targetHandle: 'target-top',
            }),
            // Seven Year Check → Seven Year Decision
            makeYesNoEdge({
                id: 'edge_seven_year_decision',
                source: NODE_IDS.CHECK_SEVEN_YEAR,
                target: NODE_IDS.DECISION_SEVEN_YEAR,
                sourceHandle: 'source-bottom',
                targetHandle: 'target-top',
            }),
            // SYR Decision Yes (record found) → Needs Review  [colors inverted via fieldType]
            makeYesNoEdge({
                id: 'edge_seven_year_review',
                source: NODE_IDS.DECISION_SEVEN_YEAR,
                target: NODE_IDS.TERMINAL_SYR_REVIEW,
                label: 'Yes',
                sourceHandle: 'source-right',
                targetHandle: 'target-left',
                fieldType: LABELS.SEVEN_YEAR_CRIMINAL_RECORD_FOUND,
            }),
            // SYR Decision No (no record) → Clear  [colors inverted via fieldType]
            makeYesNoEdge({
                id: 'edge_seven_year_clear',
                source: NODE_IDS.DECISION_SEVEN_YEAR,
                target: NODE_IDS.TERMINAL_SYR_CLEAR,
                label: 'No',
                sourceHandle: 'source-bottom',
                targetHandle: 'target-top',
                fieldType: LABELS.SEVEN_YEAR_CRIMINAL_RECORD_FOUND,
            }),
        ],
    };
}

// ─── Additional checks assembler ──────────────────────────────────────────────
//
// Reads which checks are enabled, computes their layout, and delegates to each
// section builder. Seven Year only runs when Auth DB is also enabled.
//
// Layout (T-fork from IDENTITY PROOF VALID):
//
//          [IDENTITY PROOF VALID] ──No──→ [End - Check Error]
//                     ↓ Yes
//           ┌──────────┴──────────┐
//           ↓ Yes                 ↓ Yes
//  [Global Watchlist]         [Auth DB]
//  [WATCHLIST RECORD?]        [AUTH DB PASSED?] ──No──→ [End - Check Error]
//   Yes → [Needs Review]              ↓ Yes
//   No  → [Clear]             [7 Year Criminal]
//                             [7YR RECORD?] ──Yes──→ [Needs Review]
//                                   No ↓
//                                 [Clear]

function buildAdditionalChecks(additionalChecksObj, identityProofX, identityProofY) {
    const enabledChecks = Object.values(additionalChecksObj).filter((c) => c.enabled);
    const checkTypes = enabledChecks.map((c) => c.check_type);

    const hasGlobalWatchlist = checkTypes.includes(CHECK_TYPES.GLOBAL_WATCHLIST);
    const hasAuthDb = checkTypes.includes(CHECK_TYPES.AUTHORITATIVE_DB);
    const hasSevenYear = checkTypes.includes(CHECK_TYPES.SEVEN_YEAR);

    const layout = computeAdditionalChecksLayout({
        identityProofX,
        identityProofY,
        hasGlobalWatchlist,
        hasAuthDb,
    });

    const gwSection = hasGlobalWatchlist
        ? buildGlobalWatchlistSection(layout.globalWatchlist)
        : null;
    const authDbSection = hasAuthDb ? buildAuthDbSection(layout.authDb, hasSevenYear) : null;
    // Seven Year only runs sequentially after Auth DB — never standalone
    const sevenYearSection =
        hasAuthDb && hasSevenYear ? buildSevenYearSection(layout.sevenYear) : null;

    return {
        nodes: [
            ...(gwSection?.nodes ?? []),
            ...(authDbSection?.nodes ?? []),
            ...(sevenYearSection?.nodes ?? []),
        ],
        edges: [
            ...(gwSection?.edges ?? []),
            ...(authDbSection?.edges ?? []),
            ...(sevenYearSection?.edges ?? []),
        ],
    };
}

function normalizeIdVerifications(idVerificationsArray) {
    const obj = {};

    let verificationOption = 'AND';

    for (const item of idVerificationsArray) {
        const { verification_type, ...rest } = item;

        if (verification_type === VERIFICATION_TYPES.QUICK_VERIFY) continue;

        obj[verification_type] = {
            ...rest,
            verification_type,
            metadata: item.metadata ?? {},
        };

        if (rest?.enabled && !rest?.mandatory) verificationOption = 'OR';
    }

    obj.verification_option = verificationOption;

    return obj;
}

function normalizeAdditionalChecks(additionalChecksArray) {
    const obj = {};

    const { AUTH_DB, GLOBAL_WATCHLIST, SEVEN_YEAR } = VERIFICATION_TYPES;

    const allowedAdditionalChecks = new Set([AUTH_DB, GLOBAL_WATCHLIST, SEVEN_YEAR]);

    for (const item of additionalChecksArray) {
        if (allowedAdditionalChecks.has(item.check_type)) {
            obj[item.check_type] = item;
        }
    }
    return obj;
}

// ─── Main chart data builder ──────────────────────────────────────────────────
//
// Orchestrates the full flowchart from top to bottom:
//   1. Build identity section → measure group dimensions
//   2. Center header + group on canvas
//   3. Place Identity Proof decision node below the group
//   4. Build additional checks (Global Watchlist, Auth DB, Seven Year)
//      or fall back to a direct Clear terminal if no checks are configured
//
// Returns [nodes, edges] ready to be passed to ReactFlow via useNodesState / useEdgesState.

export const prepareCustomFieldFlowChartData = (workflowConfig) => {
    if (!workflowConfig?.id_verifications) {
        console.warn('prepareChartData: missing id_verifications');
        return [[], []];
    }

    const id_verifications_obj = normalizeIdVerifications(workflowConfig.id_verifications);

    const additional_checks = normalizeAdditionalChecks(workflowConfig?.additional_checks ?? []);

    const HW = NODE_SIZE.HEADER.width; // 280
    const HH = NODE_SIZE.HEADER.height; // 64
    const DW = NODE_SIZE.DECISION.width; // 260
    const DH = NODE_SIZE.DECISION.height; // 48
    const TW = NODE_SIZE.TERMINAL.width; // 140
    const TH = NODE_SIZE.TERMINAL.height; // 40
    const SV = GAP.SECTION_V; // 60
    const AC = ADDITIONAL_CHECKS;

    // ── 1. Build identity section first to get group dimensions ──────────────
    const {
        nodes: identityNodes,
        edges: identityEdges,
        groupWidth,
        groupHeight,
    } = buildIdentitySection(id_verifications_obj);

    // ── 2. Center header and group horizontally on the canvas ────────────────
    const canvasWidth = Math.max(HW, groupWidth);
    const GROUP_Y = HH + SV;

    const headerNode = makeNode({
        id: NODE_IDS.HEADER,
        type: 'header',
        position: { x: canvasWidth / 2 - HW / 2, y: 0 },
        data: { label: 'Workflow Configuration → Custom fields' },
    });

    // Update the group node's position to its final centered coordinates
    const groupNode = identityNodes.find((n) => n.id === NODE_IDS.GROUP_IDENTITY);
    if (groupNode) {
        groupNode.position = { x: canvasWidth / 2 - groupWidth / 2, y: GROUP_Y };
    }

    // ── 3. Identity Proof Valid decision node ────────────────────────────────
    const IDENTITY_PROOF_Y = GROUP_Y + groupHeight + SV + 40;
    const identityProofX = canvasWidth / 2 - DW / 2;

    const identityProofNode = makeNode({
        id: NODE_IDS.DECISION_IDENTITY_PROOF,
        type: 'decision',
        // Small X adjustment to visually center the decision node under the canvas
        position: { x: identityProofX + AC.IDENTITY_PROOF_X_ADJ, y: IDENTITY_PROOF_Y },
        data: { label: LABELS.IDENTITY_PROOF_VALID },
    });

    // Connect the identity group bottom handle to the decision node
    const groupToIdentityProofEdge = makeYesNoEdge({
        id: 'edge_group_to_identity_proof',
        source: NODE_IDS.GROUP_IDENTITY,
        target: NODE_IDS.DECISION_IDENTITY_PROOF,
        sourceHandle: 'source-bottom',
        targetHandle: 'target-top',
    });

    // Identity Proof No → End Check Error (exits to the right)
    const identityProofErrorNode = makeNode({
        id: NODE_IDS.TERMINAL_IDENTITY_PROOF_ERROR,
        type: 'terminal',
        position: {
            x: identityProofX + DW + GAP.TERM_H_OFFSET + AC.IDENTITY_PROOF_ERROR_X_EXTRA,
            y: IDENTITY_PROOF_Y + (DH - TH) / 2,
        },
        data: { label: LABELS.END_CHECK_ERROR, variant: 'checkError' },
    });

    const identityProofErrorEdge = makeYesNoEdge({
        id: 'edge_identity_proof_error',
        source: NODE_IDS.DECISION_IDENTITY_PROOF,
        target: NODE_IDS.TERMINAL_IDENTITY_PROOF_ERROR,
        label: 'No',
        sourceHandle: 'source-right',
        targetHandle: 'target-left',
    });

    // ── 4. Additional checks or fallback Clear terminal ───────────────────────
    // When additional checks are configured, build each branch below the
    // Identity Proof decision. When none are enabled, wire Yes directly to Clear.
    const enabledAdditionalChecks = additional_checks
        ? Object.values(additional_checks).filter((c) => c.enabled)
        : [];
    const hasAdditionalChecks = enabledAdditionalChecks.length > 0;

    const additionalChecksSection = hasAdditionalChecks
        ? buildAdditionalChecks(
              additional_checks,
              identityProofX - 10,
              IDENTITY_PROOF_Y + AC.SECTION_Y_OFFSET
          )
        : null;

    // No additional checks — Identity Proof Yes goes directly to Clear
    const fallbackClearNode = !hasAdditionalChecks
        ? makeNode({
              id: NODE_IDS.TERMINAL_IDENTITY_PROOF_CLEAR,
              type: 'terminal',
              position: {
                  x: identityProofX + AC.IP_CLEAR_X_ADJ + (DW - TW) / 2,
                  y: IDENTITY_PROOF_Y + DH + SV + AC.IP_CLEAR_Y_EXTRA,
              },
              data: { label: LABELS.CLEAR, variant: 'clear' },
          })
        : null;

    const fallbackClearEdge = !hasAdditionalChecks
        ? makeYesNoEdge({
              id: 'edge_identity_proof_clear',
              source: NODE_IDS.DECISION_IDENTITY_PROOF,
              target: NODE_IDS.TERMINAL_IDENTITY_PROOF_CLEAR,
              label: 'Yes',
              sourceHandle: 'source-bottom',
              targetHandle: 'target-top',
          })
        : null;

    const nodes = [
        headerNode,
        ...identityNodes,
        identityProofNode,
        identityProofErrorNode,
        fallbackClearNode,
        ...(additionalChecksSection?.nodes ?? []),
    ].filter(Boolean);

    const edges = [
        ...identityEdges,
        groupToIdentityProofEdge,
        identityProofErrorEdge,
        fallbackClearEdge,
        ...(additionalChecksSection?.edges ?? []),
    ].filter(Boolean);

    return [nodes, edges];
};
